import { useEffect, useState } from 'react';

// Purely cosmetic, client-side "what's happening" messages shown while a job
// is PROCESSING — cycled on a timer, not tied to real backend progress (the
// backend only reports a coarse percent plus best-effort token/char counts,
// see formatGenerationProgress in jobDisplay.ts). Mirrors the kind of ambient
// status text coding agents print while working, so a long-running job
// doesn't look stuck. `{docType}` is replaced with the job's resolved document
// type name when known, else a generic fallback.
const PROCESSING_FLAVOR_MESSAGES: Record<string, string[]> = {
  GENERATE_DOCUMENT: [
    'Outlining {docType}…',
    'Drafting the opening section…',
    'Researching supporting details…',
    'Writing section content…',
    'Refining structure…',
    'Polishing language…',
  ],
  GENERATE_DOCUMENT_SECTIONS: [
    'Drafting sections for {docType}…',
    'Writing section content…',
    'Cross-checking related documents…',
    'Refining structure…',
    'Polishing language…',
  ],
  GENERATE_DOCUMENT_QUESTIONS: [
    'Reviewing {docType} requirements…',
    'Identifying open questions…',
    'Drafting clarifying questions…',
  ],
  GENERATE_DOCUMENT_METADATA: [
    'Reading through {docType}…',
    'Building dashboard cards…',
    'Summarizing key metrics…',
  ],
  SCORE_DOCUMENT_SECTIONS: [
    'Reading through {docType}…',
    'Weighing the evidence…',
    'Scoring each question…',
  ],
  EXTRACT_DOCUMENT_ENTITIES: [
    'Reading through {docType}…',
    'Extracting key entities…',
    'Cross-referencing existing entities…',
  ],
  GENERATE_DOCUMENT_VISUALIZATION: [
    'Analyzing section data…',
    'Choosing a chart type…',
    'Designing the visualization…',
    'Rendering chart…',
  ],
  GENERATE_DOCUMENT_PRESENTATION: [
    'Outlining slides…',
    'Drafting slide content…',
    'Selecting visuals…',
    'Assembling deck…',
  ],
  GENERATE_DOCUMENT_SECTION: ['Reading current section…', 'Applying requested changes…', 'Refining wording…'],
  ANALYZE_DOCUMENT_SECTION_CHANGE_IMPACT: ['Scanning related sections…', 'Evaluating downstream impact…'],
  GENERATE_ENTITY_TYPE_ENRICHMENT: ['Researching entity background…', 'Drafting enrichment content…'],
  GENERATE_VALIDATION_SUMMARY: [
    'Reading all the stages…',
    'Scoring each question…',
    'Weighing strengths against risks…',
    'Drafting the verdict…',
    'Checking the numbers…',
  ],
  ANALYZE_PROJECT_SOURCES: [
    'Reading the company website…',
    'Reading the other sources…',
    'Researching the company…',
    'Checking the company\'s claims…',
    'Composing the brief…',
  ],
};
const DEFAULT_FLAVOR_MESSAGES = ['Working…', 'Processing…', 'Almost there…'];
const FLAVOR_MESSAGE_MIN_INTERVAL_MS = 3000;
const FLAVOR_MESSAGE_MAX_INTERVAL_MS = 5000;

function randomFlavorIntervalMs(): number {
  return FLAVOR_MESSAGE_MIN_INTERVAL_MS + Math.random() * (FLAVOR_MESSAGE_MAX_INTERVAL_MS - FLAVOR_MESSAGE_MIN_INTERVAL_MS);
}

// Cycles through PROCESSING_FLAVOR_MESSAGES[jobType] on a timer while
// `active`. Starts at a random offset so multiple chips visible at once
// don't all show the same message in lockstep. Each step waits a fresh
// random 3-5s (setTimeout, not setInterval) so the cadence itself feels
// non-mechanical too.
export function useJobFlavorMessage(jobType: string, docTypeName: string | null, active: boolean): string | null {
  const pool = (PROCESSING_FLAVOR_MESSAGES[jobType] ?? DEFAULT_FLAVOR_MESSAGES).map((m) =>
    m.replace('{docType}', docTypeName ?? 'this document'),
  );
  const [index, setIndex] = useState(() => Math.floor(Math.random() * pool.length));

  useEffect(() => {
    if (!active) return;
    let id: ReturnType<typeof setTimeout>;
    const tick = () => {
      setIndex((i) => (i + 1) % pool.length);
      id = setTimeout(tick, randomFlavorIntervalMs());
    };
    id = setTimeout(tick, randomFlavorIntervalMs());
    return () => clearTimeout(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, pool.length, jobType]);

  return active ? (pool[index % pool.length] ?? null) : null;
}
